import { computed, reactive, ref, watch } from "vue";
import { defineStore } from "pinia";
import axiosRepo from "@/repositories/AxiosRepository";
import cytoscape from "cytoscape";
import fcose from "cytoscape-fcose";
import cytoStyle from "@/components/cyto_graph_components/cytoStyle.json";

export const useQueryStore = defineStore("query", () => {
  /*** STATE VARIABLES ***/
  const tabValue = ref("chat");
  const exploreData = ref();
  const joinStack = reactive({ data: [] });
  const queryCols = reactive({
    data: [],
    keep: computed(() => queryCols.data.filter((col) => col.list === "keep")),
    measure: computed(() =>
      queryCols.data.filter((col) => col.list === "measure")
    ),
    filter: computed(() =>
      queryCols.data.filter((col) => col.list === "filter")
    ),
  });
  const populations = reactive({ data: {} });
  const resultCols = ref([]);
  const uniqueQueryColIndex = ref(0);
  const uniqueJoinStackTableIndex = ref(0);
  const queryRunning = ref(false);
  const results = ref({
    table_path: "",
    total_rows: 0,
  });
  let cyMain = null;
  let cytoData = null;
  let selectedNode = null;
  let previousNode = null;
  let neighbours = null;
  let chatThreadId = (Math.random() + 1).toString(36);

  class NonAdjacentNodeError extends Error {
    constructor(
      message = "Attempted to add node that was not connected " +
        "to the previous node in the graph"
    ) {
      super(message);
      this.name = "NonAdjacentNodeError";
    }
  }

  /*** HELPER FUNCTIONS ***/
  const countNodeOccurrences = (node) =>
    joinStack.data.filter(
      (obj) => obj.name + obj.suffix === node.name + node.suffix
    ).length;

  const getAllKeyIndices = () => {
    let nodeMap = {};
    joinStack.data.forEach((obj, index) => {
      let objKeys = getNodeKeys(obj);
      objKeys = objKeys.sort((a, b) => a.name.localeCompare(b.name));
      objKeys = objKeys.map((key) => key.name + obj.suffix);
      if (nodeMap[objKeys]) {
        nodeMap[objKeys].push(index);
      } else {
        nodeMap[objKeys] = [index];
      }
    });
    return nodeMap;
  };

  const getNodeKeys = (node) => {
    const keyCols = node.columns.filter((col) =>
      col.Key.some((key) => ["PK", "FK"].includes(key))
    );
    return keyCols.map((col) => {
      return {
        name: col.name + node.suffix,
        type: col.name.split("-")[0],
      };
    });
  };

  const suffixNeeded = (node) => {
    // Check if the node needs a suffix to prevent confusion with other nodes
    // Previously this check was as simple as does the node exist more than once
    // but this is not sufficient as two different tables with the same node
    // types can also trip up column resolution. e.g.
    // Protein_interactsWith_Protein [Protein__1, Protein__2] and
    // Protein_homologousTo_Protein [Protein__1, Protein__2].
    //
    let nodeKeys = getNodeKeys(node);
    nodeKeys = nodeKeys.sort((a, b) => a.name.localeCompare(b.name));
    nodeKeys = nodeKeys.map((key) => key.name + node.suffix);
    let nodeMap = getAllKeyIndices();
    return nodeMap[nodeKeys].length > 1;
  };

  const getPossibleSuffixesOfColName = (colname) =>
    joinStack.data
      .filter((obj) => obj.columns.some((col) => col.name === colname))
      .map((obj) => obj.suffix);

  /*** JOIN STACK OPERATIONS ***/
  function joinStackAdd(nodeData) {
    const suffix = ref(nodeData.suffix || "");
    joinStack.data.push({
      ...nodeData,
      key: uniqueJoinStackTableIndex.value.toString(),
      suffix: suffix,
      label: computed(() => nodeData.name + (suffix.value || "")),
      treeNodeType: "table",
      children: nodeData.columns.map((column, index) => ({
        ...column,
        key: `${uniqueJoinStackTableIndex.value}-${index}`,
        label: computed(() => column.name + (suffix.value || "")),
        treeNodeType: "column",
      })),
    });
    uniqueJoinStackTableIndex.value++;
  }

  function addNodeToJoinStackById(nodeId) {
    addNodeToJoinStack(cyMain.getElementById(nodeId));
  }

  function addNodeToJoinStack(node) {
    console.debug("addNodeToJoinStack", node.id());
    if (!cyMain) return;
    selectedNode = node;
    const isPropertyNode = selectedNode.data("type") === "Property";
    const isFirstNode = previousNode === null;
    const isAdjacentNode = neighbours?.contains(selectedNode);
    if (!isFirstNode && !isAdjacentNode) throw new NonAdjacentNodeError();

    if (!selectedNode) {
      console.error("Cytoscape Nodes", cytoData);
      throw new Error(
        `Could not find node with id ${node.id()} in Cytoscape nodes`
      );
    }
    if (selectedNode && selectedNode.length) {
      joinStackAdd({ ...selectedNode.data() });
      resetUI();

      cyMain.elements().forEach((e) => e.addClass("transparency"));

      if (!isFirstNode) {
        previousNode.edgesTo(selectedNode)[0]?.addClass("traversed") ||
          selectedNode.edgesTo(previousNode)[0]?.addClass("traversed");
      }

      if (!isFirstNode && isPropertyNode) {
        previousNode.addClass("selected");
        neighbours = previousNode.neighborhood();
      } else {
        selectedNode.addClass("selected traversed");
        previousNode = selectedNode;
        neighbours = selectedNode.neighborhood();
      }
      neighbours.forEach((e) => e.addClass("neighbour"));
    }
  }

  /*** QUERY COLUMN OPERATIONS ***/
  function findColumnsByLabel(label) {
    /* Helper to find columns by label in joinStack */
    return joinStack.data.flatMap((tab) =>
      tab.children.filter((col) => col.label === label)
    );
  }

  function addQueryColByLabel(
    label,
    list = "keep",
    {
      measure_type = "count",
      filter_operator = "IS NOT",
      filter_value = ["NULL"],
    } = {}
  ) {
    console.debug("addQueryColByLabel", label);
    const matches = findColumnsByLabel(label);

    addQueryCol(
      matches[0].key,
      list,
      measure_type,
      filter_operator,
      filter_value
    );
  }

  function addQueryCol(
    key,
    list = "keep",
    measure_type = "count",
    filter_operator = "IS NOT",
    filter_value = ["NULL"]
  ) {
    console.debug(
      "addQueryCol",
      key,
      list,
      measure_type,
      filter_operator,
      filter_value
    );
    if (!key.includes("-")) return;
    const [tableKey] = key.split("-");
    const table = joinStack.data.find((tab) => tab.key === tableKey);
    const column = table?.children.find((col) => col.key === key);
    if (!column) return;

    queryCols.data.push({
      column,
      table,
      uniqueKey: uniqueQueryColIndex.value++,
      measure_type,
      filter_operator,
      filter_value,
      list,
    });
  }

  function removeQueryCol(uniqueKey) {
    queryCols.data = queryCols.data.filter(
      (c) => c.uniqueKey !== parseInt(uniqueKey)
    );
  }

  /*** POPULATION OPERATIONS ***/
  function openPopulation(data) {
    data.user_filters.forEach((filter) => (filter.suffix = ""));
    populations.data[data.name] = data;
  }

  function closePopulation(data) {
    delete populations.data[data.name];
  }

  /*** WATCHERS ***/
  watch(
    () => joinStack.data,
    () => {
      Object.values(populations.data).forEach((pop) => {
        pop.user_filters.forEach((filter) => {
          filter.suffix_options = getPossibleSuffixesOfColName(filter.column);
        });
      });
    },
    { deep: true }
  );

  /*** STORE RESET ***/
  function clearQueryStore() {
    joinStack.data = [];
    queryCols.data = [];
    populations.data = {};
    uniqueQueryColIndex.value = 0;
    uniqueJoinStackTableIndex.value = 0;
  }

  function resetUI(hard = false) {
    cyMain.nodes().forEach((node) => node.removeClass("neighbour selected"));
    if (hard) {
      cyMain.elements().forEach((e) => e.removeClass("traversed transparency"));
      clearQueryStore();
      selectedNode = previousNode = neighbours = null;
    }
  }

  /*** CYTOSCAPE INITIALIZATION ***/
  async function initCy(container) {
    cytoData = await axiosRepo.get_schema("Bo_live");
    cytoscape.use(fcose);
    cyMain = cytoscape({
      container: container,
      elements: cytoData["data"],
      style: cytoStyle,
      layout: {
        name: "fcose",
        nodeRepulsion: 30000,
        animate: false,
        fit: true,
        randomize: true,
      },
    });

    cyMain.on("tap", "node", (event) => {
      if (tabValue.value === "explore") {
        exploreData.value = event.target.data();
        console.debug("set table data", exploreData.value);
      } else {
        try {
          addNodeToJoinStack(event.target);
        } catch (err) {
          if (err instanceof NonAdjacentNodeError) {
            console.info(
              "Did not add node as it is not connected to " +
                "the current graph"
            );
          } else {
            throw err; // unknown error, rethrow it (**)
          }
        }
      }
    });

    cyMain.on("dblclick", () => {
      resetUI(true);
    });
  }

  async function runQuery() {
    queryRunning.value = true;
    console.debug(
      "triggerRunQuery",
      joinStack.data,
      queryCols.data,
      populations.data
    );
    try {
      let res = await axiosRepo.run_query(
        joinStack.data,
        queryCols.data,
        populations.data
      );
      console.debug("Received query results: ", res);
      if (res.status === 200) {
        results.value = res.data;
        tabValue.value = "results";
      }
    } finally {
      queryRunning.value = false;
    }
  }

  return {
    tabValue,
    exploreData,
    joinStack,
    queryCols,
    resultCols,
    populations,
    queryRunning,
    results,
    chatThreadId,
    countNodeOccurrences,
    suffixNeeded,
    getPossibleSuffixesOfColName,
    addNodeToJoinStackById,
    addQueryCol,
    addQueryColByLabel,
    removeQueryCol,
    openPopulation,
    closePopulation,
    resetUI,
    initCy,
    runQuery,
  };
});
